import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faCalendarDays } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import React, { type ReactElement, useEffect } from 'react'
import type { InputInFormProps } from '~/components/Form/Input/input'
import {
  Datepicker,
  type DatepickerProps
} from './Elements/Datepicker/Datepicker'
import {
  errorMessage,
  halfWidth,
  inputField,
  inputHasLeadingText,
  inputLabel,
  inputLeadingIcon,
  inputLeadingText,
  inputTrailingIcon,
  inputTrailingIconClickable,
  inputWrapper
} from './TextInput.css'

type DateInputProps = InputInFormProps &
  Omit<DatepickerProps, 'onChange' | 'currentDate'> & {
    label?: string
    name?: string
    startName?: string
    endName?: string
    defaultDate?: Date | null
    onChange?: (date: Date | null) => void
    required?: boolean
    disabled?: boolean
    wrapper?: boolean
    fullWidth?: boolean
    inputSize?: 'small' | 'extraSmall'
    leadingIcon?: IconDefinition
    leadingText?: string
  }

export const DateInput = ({
  register,
  label,
  name,
  startName,
  endName,
  defaultDate,
  onChange,
  error,
  required,
  wrapper = true,
  fullWidth = true,
  inputSize,
  leadingIcon,
  leadingText,
  disabled,
  watch,
  setValue,
  ...props
}: DateInputProps): ReactElement => {
  const [date, setDate] = React.useState<Date | null>(defaultDate ?? null)

  useEffect(() => {
    if (defaultDate !== undefined) {
      setDate(defaultDate)
    }
  }, [defaultDate])

  const datepickerProps: Omit<DatepickerProps, 'onChange' | 'currentDate'> =
    props

  let inputValue: Date | null = null

  if (
    register !== undefined &&
    setValue !== undefined &&
    name !== undefined &&
    watch !== undefined
  ) {
    register(name)
    inputValue = watch(name)

    if (startName && endName) {
      if (startName === name) {
        datepickerProps.selectsStart = true
      }
      if (endName === name) {
        datepickerProps.selectsEnd = true
        datepickerProps.minDate = watch(startName)
      }
      datepickerProps.startDate = watch(startName)
      datepickerProps.endDate = watch(endName)
    }
  }

  const onDateChange = (date: Date | null) => {
    setDate(date)
    if (setValue !== undefined && name !== undefined) {
      setValue(name, date, {
        shouldDirty: true,
        shouldValidate: true
      })
    }

    onChange?.(date)
  }

  useEffect(() => {
    if (inputValue !== undefined) {
      setDate(inputValue)
    }
  }, [inputValue])

  // A prefilled date is part of the submitted values, like a typed one
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on mount, later changes go through onDateChange
  useEffect(() => {
    if (
      setValue !== undefined &&
      name !== undefined &&
      defaultDate &&
      inputValue == null
    ) {
      setValue(name, defaultDate, { shouldValidate: false })
    }
  }, [])

  const Input = (
    <>
      {leadingIcon && (
        <div className={inputLeadingIcon}>
          <FontAwesomeIcon icon={leadingIcon} />
        </div>
      )}
      {leadingText && <div className={inputLeadingText}>{leadingText}</div>}
      <Datepicker
        name={name}
        currentDate={date}
        onChange={onDateChange}
        inputSize={inputSize}
        disabled={disabled}
        {...datepickerProps}
      />
      <div className={clsx(inputTrailingIcon, inputTrailingIconClickable)}>
        <FontAwesomeIcon icon={faCalendarDays} />
      </div>
    </>
  )

  if (!wrapper) {
    return (
      <div
        className={clsx(
          inputField,
          fullWidth ? null : halfWidth,
          leadingText && inputHasLeadingText
        )}
      >
        {Input}
      </div>
    )
  }

  return (
    <div className={clsx(inputWrapper, fullWidth ? null : halfWidth)}>
      {label && (
        <label htmlFor={name} className={inputLabel}>
          {label}
        </label>
      )}
      <div
        className={clsx(inputField, leadingText && inputHasLeadingText)}
        aria-invalid={error !== null ? 'true' : 'false'}
      >
        {Input}
      </div>
      {error && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )
}
